if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mediaRevealPromises = new WeakMap();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      async setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const currentActiveMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (currentActiveMedia === activeMedia) {
          const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) this.setActiveThumbnail(activeThumbnail);
          return;
        }

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        const revealPromise = this.prepareMediaForReveal(activeMedia);
        this.prepareThumbnailForReveal(activeThumbnail);

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
          element.setAttribute('aria-hidden', 'true');
        });
        activeMedia.classList.add('is-active');
        activeMedia.setAttribute('aria-hidden', 'false');
        this.preloadAdjacentMedia(activeMedia);

        if (prepend) {
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.requestAnimationFrame(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        if (currentActiveMedia && currentActiveMedia !== activeMedia) this.pauseInactiveMedia(currentActiveMedia);

        if (this.elements.thumbnails && activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        await revealPromise;
      }

      getMediaImage(mediaElement) {
        return mediaElement?.querySelector('.product__media img, .deferred-media__poster img, img');
      }

      async prepareMediaForReveal(mediaElement) {
        const image = this.getMediaImage(mediaElement);
        if (!image) return;

        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'high';

        if (image.complete && image.naturalWidth > 0) {
          if (typeof image.decode === 'function') {
            try {
              await image.decode();
            } catch (error) {}
          }
          return;
        }

        if (this.mediaRevealPromises.has(image)) {
          await this.mediaRevealPromises.get(image);
          return;
        }

        const revealPromise = new Promise((resolve) => {
          const finalize = () => {
            if (typeof image.decode === 'function') {
              image
                .decode()
                .catch(() => {})
                .finally(resolve);
              return;
            }
            resolve();
          };

          image.addEventListener('load', finalize, { once: true });
          image.addEventListener('error', resolve, { once: true });
        }).finally(() => this.mediaRevealPromises.delete(image));

        this.mediaRevealPromises.set(image, revealPromise);
        await revealPromise;
      }

      prepareThumbnailForReveal(thumbnail) {
        const image = thumbnail?.querySelector('img');
        if (!image) return;

        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'low';

        if (image.complete && image.naturalWidth > 0) return;

        const thumbSrc = image.currentSrc || image.src;
        if (!thumbSrc) return;

        const preload = new Image();
        preload.decoding = 'async';
        preload.loading = 'eager';
        preload.fetchPriority = 'low';
        preload.src = thumbSrc;
      }

      preloadAdjacentMedia(activeMedia) {
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const activeIndex = mediaItems.indexOf(activeMedia);
        if (activeIndex === -1) return;

        [mediaItems[activeIndex - 1], mediaItems[activeIndex + 1]].forEach((mediaElement) => {
          const image = this.getMediaImage(mediaElement);
          if (!image) return;
          image.loading = 'eager';
          if (image.complete && image.naturalWidth > 0 && typeof image.decode === 'function') {
            image.decode().catch(() => {});
          }
        });
      }

      pauseInactiveMedia(mediaElement) {
        const video = mediaElement?.querySelector('video');
        if (video && !video.paused) video.pause();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        window.clearTimeout(this.liveRegionTimeout);
        this.liveRegionTimeout = window.setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    },
  );
}
